import { useLocation } from "wouter";
import { archiveProjectSchema, HandleSaveArchiveValues, Project } from "@/api/studio/projects/types/project.type";
import { Button } from "@/components/ui/button";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useArchiveProject } from "@/api/studio/projects/queries";
import { Form } from "@/components/ui/form/form";
import { TextInput } from "@/components/ui/form/text-input";
import { useModalError } from "@/hooks/use-modal-error";

/* Prop Types */
interface ArchiveProjectProps {
  project: Project;
}

/*
 * ArchiveProjectModal component
 * This component is used to archive a project
 */
export const ArchiveProjectModal = ({ project }: ArchiveProjectProps) => {
  /* Stores */
  const [, navigate] = useLocation();

  /* Queries */
  const { mutateAsync: archiveProject, isPending: isUpdateProjectPending, isError: isUpdateProjectError } = useArchiveProject();

  /* Hooks */
  const { close } = useModalError({ error: isUpdateProjectError });

  /* Handlers */
  const handleSave = async (values: HandleSaveArchiveValues) => {
    await archiveProject({
      id: project.id,
      values: {
        ...project,
        isArchived: values.isArchived,
      },
    });
    navigate("/projects");
    close();
  };

  /* Render */
  return (
    <Form onSubmit={handleSave} schema={archiveProjectSchema} options={{ defaultValues: { isArchived: true } }}>
      {({ register }) => (
        <>
          {/* Header */}
          <div className="flex items-center justify-between pb-4">
            <h2>Archive project</h2>
            <XMarkIcon onClick={close} className="size-5 cursor-pointer" />
          </div>

          {/* Details */}
          <div className="pb-4">
            <p>This project will be archived. You can find it later in the ‘Archived’ tab on the projects page. You can restore it anytime.</p>
          </div>
          <TextInput type="hidden" registration={register("isArchived")} className="mb-3" />

          {/* Buttons */}
          <div className="flex justify-end pt-4">
            <Button type="submit" variant="primary" disabled={isUpdateProjectPending}>
              {isUpdateProjectPending ? "Archiving..." : "Archive project"}
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};
